// ============================================================
// FILE: DetailToolbar.js
// LOCATION: C:\gardenmarket\web\src\components\DetailToolbar.js
// ============================================================
// PURPOSE:
//   Shared action toolbar rendered at the top of both the buyer
//   job detail pane (JobDetailContent.js) and the gardener
//   booking detail pane (BookingDetailContent.js). Groups all
//   status-dependent action buttons in one consistent strip so
//   both pages look and behave identically.
//
// PROPS:
//   actions — array of action objects:
//     { label, onClick, href, style }
//     style: 'primary' | 'secondary' | 'danger' | 'info'
//     Use href for Link-based navigation, onClick for handlers.
//
// USAGE:
//   <DetailToolbar actions={[
//     { label: '✏️ Edit', href: `/buyer/job/${id}/edit`, style: 'secondary' },
//     { label: 'Mark as Completed', onClick: handleComplete, style: 'primary' },
//     { label: 'Withdraw Job', onClick: handleWithdraw, style: 'danger' },
//   ]} />
// ============================================================

import { Link } from 'react-router-dom';
import { colors, radius } from '../theme/theme';

const STYLES = {
    primary:   { backgroundColor: colors.brand,  color: 'white',  border: 'none' },
    secondary: { backgroundColor: 'white', color: colors.brand,  border: `1px solid ${colors.brand}` },
    danger:    { backgroundColor: 'white', color: '#c0392b', border: '1px solid #e8b4ac' },
    info:      { backgroundColor: '#00BCD4', color: 'white', border: 'none' },
};

export default function DetailToolbar({ actions = [] }) {
    if (!actions || actions.length === 0) return null;

    return (
        <div style={styles.toolbar}>
            {actions.map((action, i) => {
                const btnStyle = {
                    ...styles.btn,
                    ...STYLES[action.style || 'secondary'],
                    ...(action.disabled ? styles.disabled : {}),
                };

                if (action.href) {
                    return (
                        <Link key={i} to={action.href} style={btnStyle} className="gh-touch-target">
                            {action.label}
                        </Link>
                    );
                }

                return (
                    <button
                        key={i}
                        onClick={action.onClick}
                        style={btnStyle}
                        disabled={action.disabled}
                        type="button"
                        className="gh-touch-target"
                    >
                        {action.label}
                    </button>
                );
            })}
        </div>
    );
}

const styles = {
    toolbar:  {
        display:        'flex',
        gap:            '8px',
        flexWrap:       'wrap',
        padding:        '12px 16px',
        backgroundColor: '#f8fafa',
        borderBottom:   `1px solid ${colors.border}`,
        marginBottom:   '20px',
        borderRadius:   `${radius.md} ${radius.md} 0 0`,
    },
    btn: {
        // .gh-touch-target in responsive.css raises this to a 44px
        // minimum on touch devices; desktop keeps the tighter size.
        padding:      '9px 18px',
        borderRadius: radius.sm,
        fontSize:     '0.85rem',
        fontWeight:   600,
        cursor:       'pointer',
        textDecoration: 'none',
        display:      'inline-block',
        whiteSpace:   'nowrap',
    },
    disabled: {
        opacity: 0.5,
        cursor:  'not-allowed',
    },
};
